import { useTranslation as useReactI18nextTranslation } from 'react-i18next';
import { useEffect, useCallback, useMemo } from 'react';
import { changeLanguage } from '../../config/i18next.config';
import { authService } from '@root/app/shared/new-services/auth.service';
import { getAppLanguages } from '@root/app/shared/new-services/languages.service';
import { getIndustryTypesList } from '@root/app/shared/new-services/company.service';
import { resolveLangValue } from '@root/app/utilities/multilanguage.utilities';
import { fetchIndexTerminologyOnce, getCachedIndexTerminology } from './useIndexTerminology';
import { resolveTerminologyKey } from './resolveTerminologyKey';

/**
 * Translation hook for React components (i18next is the source of truth).
 */
export const useTranslation = () => {
    const { t, i18n, ready } = useReactI18nextTranslation();

    useEffect(() => {
        if (!ready) {
            return;
        }
        const lang = authService.getLanguage();
        if (lang && lang !== i18n.language) {
            changeLanguage(lang);
        }
    }, [ready, i18n.language]);

    // Warm the index-terminology cache so translate() (synchronous) can pick the Wellbeing wording
    // once the flag resolves. Until then getCachedIndexTerminology() returns HAPPINESS (the default).
    useEffect(() => {
        fetchIndexTerminologyOnce();
    }, []);

    const translate = useCallback((key: string, options?: Record<string, unknown>): string => {
        // For companies on WELLBEING, resolve the "<key>_WI" sibling when it exists; otherwise fall
        // back to the exact base key so HAPPINESS/unset companies keep their current wording.
        const resolvedKey = resolveTerminologyKey(
            key,
            getCachedIndexTerminology(),
            (candidateKey) => i18n.exists(candidateKey)
        );
        if (options) {
            const result = t(resolvedKey, options);
            return typeof result === 'string' ? result : String(result);
        }
        const result = t(resolvedKey);
        return typeof result === 'string' ? result : String(result);
    }, [t, i18n]);

    const changeLanguageSync = useCallback((lang: string) => {
        changeLanguage(lang);
    }, []);

    const getCurrentLanguage = useCallback((): string => {
        return i18n.language || authService.getLanguage() || 'en';
    }, [i18n.language]);

    const getAvailableLanguages = useCallback((): string[] => {
        return Object.keys(i18n.options.resources || {});
    }, [i18n.options.resources]);

    return {
        t: translate,
        i18n,
        ready,
        changeLanguage: changeLanguageSync,
        getCurrentLanguage,
        getAvailableLanguages,
        instant: translate
    };
};

/**
 * Hook for handling multi-language text objects (compatible with your existing structure)
 */
export const useAppLanguages = () => {
    const { t } = useTranslation();
    return useMemo(() => getAppLanguages(t), [t]);
};

export const useIndustryTypes = () => {
    const { t } = useTranslation();
    return useMemo(() => getIndustryTypesList(t), [t]);
};

export const useMultiLanguageText = () => {
    const { getCurrentLanguage } = useTranslation();

    const getTranslationFromUserLanguage = useCallback((
        languageItem: string | { defaultValue: string; langValues?: { [key: string]: string } } | undefined,
        userLanguage?: string
    ): string => {
        if (!languageItem) {
            return '';
        }

        if (typeof languageItem === 'string') {
            return languageItem;
        }

        const currentLang = userLanguage || getCurrentLanguage();
        const translated = resolveLangValue(languageItem.langValues, currentLang);
        if (translated !== undefined) {
            return translated;
        }

        return languageItem.defaultValue || '';
    }, [getCurrentLanguage]);

    const createMultiLanguage = useCallback(() => {
        return {
            defaultValue: '',
            langValues: {}
        };
    }, []);

    const setLangValue = useCallback((
        langItem: { defaultValue: string; langValues?: { [key: string]: string } },
        value: string,
        language?: string
    ) => {
        if (language) {
            if (!langItem.langValues) {
                langItem.langValues = {};
            }
            langItem.langValues[language] = value;
        } else {
            langItem.defaultValue = value;
        }
    }, []);

    return {
        getTranslationFromUserLanguage,
        createMultiLanguage,
        setLangValue
    };
};
